import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useSEO from "@/lib/useSEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ArrowLink from "@/components/ArrowLink";
import { PEOPLE } from "@/data/site";

const FOUNDATION = [
  { index: "01", title: "Integrity", body: "Independence and professional responsibility are not negotiable — they are the reason a client's numbers can be relied upon." },
  { index: "02", title: "Responsibility", body: "We take ownership of the work we sign, treating each engagement with the seriousness its consequences deserve." },
  { index: "03", title: "Continuity", body: "Relationships built over years, and a practice designed to carry its standards into the next generation." },
];

const Firm = () => {
  useSEO({
    title: "The Firm",
    description:
      "The story of Arun Gupta & Co. — a Chartered Accountancy practice founded in Kanpur in 1990, built on integrity, professional continuity and an evolving perspective.",
    path: "/the-firm",
  });

  return (
    <>
      <PageHero
        eyebrow="The Firm"
        titleLines={["Built over time.", "Guided by principle."]}
        intro="A boutique Chartered Accountancy practice rooted in Kanpur, shaped by decades of professional relationships and a steady, considered way of working."
        testId="firm-hero"
      />

      {/* Our Story */}
      <section className="py-14 lg:py-24" data-testid="firm-story">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="overline">Our Story</div>
              <h2 className="font-serif text-navy text-3xl lg:text-4xl tracking-tight mt-5 leading-[1.14]">
                A practice with a long memory.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.1}>
              <p className="text-lg font-light leading-relaxed text-charcoal/85">
                Arun Gupta &amp; Co. began as a single Chartered Accountant's practice and grew,
                engagement by engagement, into a firm trusted by businesses and families across the
                region. Much of that growth came not from expansion for its own sake, but from
                relationships that deepened over years of careful work.
              </p>
              <p className="mt-6 text-base leading-relaxed text-charcoal/75">
                What has stayed constant is a way of working: read the numbers closely, understand
                the business they describe, and offer advice that a client can actually act on. What
                has changed is the range of that advice — broader today than it was in 1990, and
                still developing.
              </p>
            </Reveal>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 mt-12 lg:mt-16">
          <Reveal className="relative overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
            <img src="/brand/office3.png" alt="Reception and meeting area at the Arun Gupta & Co. office in Kanpur" className="img-cover" loading="lazy" />
          </Reveal>
        </div>
      </section>

      {/* Our Foundation */}
      <section className="py-14 lg:py-20 border-t border-border-subtle" style={{ background: "#EFEBE1" }} data-testid="firm-foundation">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="overline">Our Foundation</div>
            <h2 className="font-serif text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 max-w-xl leading-[1.1]">
              Three commitments that hold everything together.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14 mt-14 lg:mt-20">
            {FOUNDATION.map((f, i) => (
              <Reveal key={f.index} delay={i * 0.08}>
                <div className="border-t border-navy/20 pt-6">
                  <span className="font-serif text-brass text-2xl">{f.index}</span>
                  <h3 className="font-serif text-navy text-2xl lg:text-3xl mt-3">{f.title}</h3>
                  <p className="mt-4 text-base leading-relaxed text-charcoal/75">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Practice Today */}
      <section className="py-14 lg:py-24" data-testid="firm-today">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="overline">Practice Today</div>
              <h2 className="font-serif text-navy text-3xl lg:text-[2.7rem] tracking-tight mt-5 leading-[1.12]">
                Compliance done well — and a widening view around it.
              </h2>
            </Reveal>
            <Reveal delay={0.08} className="relative overflow-hidden mt-10" style={{ aspectRatio: "4 / 3" }}>
              <img src="/brand/office2.png" alt="A partner's desk at the Arun Gupta & Co. office in Kanpur" className="img-cover" loading="lazy" />
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <p className="text-lg font-light leading-relaxed text-charcoal/85">
                Today the firm's work spans audit and assurance, direct and indirect taxation,
                accounting and financial reporting, and business advisory. Around this established
                core, we are gradually developing capabilities in outsourced finance and strategic
                finance support.
              </p>
              <p className="mt-6 text-base leading-relaxed text-charcoal/75">
                We remain a boutique practice by design. It allows the people who do the work to
                stay close to the client, and it keeps professional judgement — not process volume —
                at the centre of every engagement.
              </p>
              <div className="mt-8">
                <ArrowLink to="/services" testId="firm-services-link">View Our Services</ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Rooted in Kanpur */}
      <section className="py-16 lg:py-24 relative overflow-hidden" style={{ background: "#17263D", color: "#F7F4EE" }} data-testid="firm-kanpur">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="overline">Rooted in Kanpur</div>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.06] tracking-tight mt-6" style={{ color: "#F7F4EE" }}>
                  Rooted in Kanpur.<br /><span className="italic text-brass">Looking beyond it.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <p className="text-lg font-light leading-relaxed text-ivory/80">
                  Kanpur is where the firm was founded and where its professional relationships are
                  strongest. That local depth — an understanding of the region's businesses and how
                  they operate — remains a genuine advantage.
                </p>
                <p className="mt-5 text-base leading-relaxed text-ivory/65">
                  At the same time, the financial questions our clients face are increasingly shaped
                  by national and international frameworks. Our perspective has widened accordingly.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Looking Forward */}
      <section className="py-14 lg:py-24" data-testid="firm-forward">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="overline">Looking Forward</div>
              <h2 className="font-serif text-navy text-3xl lg:text-4xl tracking-tight mt-5 leading-[1.14]">
                The next chapter, on a familiar foundation.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.1}>
              <p className="text-lg font-light leading-relaxed text-charcoal/85">
                The firm's direction is being shaped by its next generation — extending established
                strengths into financial analysis, management information, outsourced finance and
                strategic finance support. These are developing areas, approached with the same
                discipline that defines the rest of the practice.
              </p>
              <p className="mt-6 text-base leading-relaxed text-charcoal/75">
                It is a considered evolution: broadening what the firm can do for its clients without
                losing what has made it dependable for more than three decades.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Our People */}
      <section className="py-14 lg:py-20 border-t border-border-subtle" style={{ background: "#EFEBE1" }} data-testid="firm-people">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
              <div>
                <div className="overline">Our People</div>
                <h2 className="font-serif text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-[1.12]">
                  Experience and perspective, side by side.
                </h2>
              </div>
              <ArrowLink to="/our-people" testId="firm-people-link">Meet the Team</ArrowLink>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 mt-14">
            {PEOPLE.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <Link to="/our-people" className="group block" data-testid={`firm-person-${p.id}`}>
                  <div className="relative overflow-hidden" style={{ aspectRatio: "4 / 5", background: "#E7E2D8" }}>
                    <img src={p.image} alt={`${p.name}, ${p.role}`} className="img-cover" style={{ objectPosition: p.objectPosition }} loading="lazy" />
                  </div>
                  <h3 className="font-serif text-navy text-2xl lg:text-3xl mt-6">{p.name}</h3>
                  <div className="overline mt-2">{p.role}</div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Firm;
